import { ChevronDown, ChevronRight, Download, FileUp, RefreshCw, Replace, Search, Trash2 } from 'lucide-react';
import { Fragment, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Confirm } from '@/components/ui/Confirm';
import { Pill } from '@/components/ui/Pill';
import { useSession } from '@/lib/auth/useSession';
import { formatBytes, formatDate } from '@/lib/format';
import {
  COLLECTIONS,
  downloadUrl,
  fileNameChanged,
  isSupportedFile,
  SUPPORTED_EXTENSIONS,
  UNSUPPORTED_HINT,
  type DocumentCollection,
  type LibraryDocument,
  type Property,
} from './api';
import { useDeleteDocument, useDocuments, useProperties, useReindexDocument, useReplaceDocument } from './queries';
import { needsTenant, tenantLabel, UploadPanel } from './UploadPanel';

export interface TenantGroup {
  key: string;
  label: string;
  docs: LibraryDocument[];
}

export interface PropertyGroup {
  key: string;
  label: string;
  count: number;
  /** One unlabelled group when the collection isn't split by tenant. */
  tenants: TenantGroup[];
}

const byName = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);
const WHOLE = 'whole-property';

/** Property, then tenant (whole-property documents first), then document name. */
export function groupDocuments(docs: LibraryDocument[], properties: Property[], byTenant: boolean): PropertyGroup[] {
  const names = new Map(properties.map((p) => [p.key, p.name]));
  const props = new Map<string, Map<string, TenantGroup>>();
  for (const d of docs) {
    const tenants = props.get(d.propertyKey) ?? new Map<string, TenantGroup>();
    props.set(d.propertyKey, tenants);
    const key = byTenant ? (d.party?.id ?? WHOLE) : '';
    const label = byTenant ? (d.party ? tenantLabel(d.party) : 'Whole property') : '';
    const g = tenants.get(key) ?? { key, label, docs: [] };
    g.docs.push(d);
    tenants.set(key, g);
  }
  return [...props.entries()]
    .map(([key, tenants]) => {
      const groups = [...tenants.values()];
      for (const g of groups) g.docs.sort((a, b) => a.name.localeCompare(b.name));
      groups.sort((a, b) => (a.key === WHOLE ? -1 : b.key === WHOLE ? 1 : byName(a, b)));
      return {
        key,
        label: names.get(key) ?? key,
        count: groups.reduce((n, g) => n + g.docs.length, 0),
        tenants: groups,
      };
    })
    .sort(byName);
}

/**
 * Document library (P1-D8): upload, list, replace, delete. Indexed documents
 * are searchable by Simon in chat.
 */
export function DocumentsScreen() {
  const [collection, setCollection] = useState<DocumentCollection>('lease_documents');
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const [propertyKeys, setPropertyKeys] = useState<string[]>([]); // [] = every property
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const { locked } = useSession();
  const properties = useProperties();
  const docs = useDocuments(collection, q.trim(), propertyKeys);
  const groups = groupDocuments(docs.data ?? [], properties.data ?? [], needsTenant(collection));
  const toggle = (key: string) =>
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink">Documents</h1>
            <p className="mt-1 text-sm text-ink-soft">
              Indexed documents are searchable by Simon and cited in its answers.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void docs.refetch()}
              disabled={docs.isFetching}
              aria-label="Refresh list"
              title="Refresh list"
            >
              <RefreshCw size={16} className={docs.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button onClick={() => setUploading(true)} lockedReason={locked('documents.write')}>
              <FileUp size={16} /> Upload
            </Button>
          </div>
        </div>

        {uploading && <UploadPanel collection={collection} onClose={() => setUploading(false)} />}

        <PropertyFilter properties={properties.data ?? []} selected={propertyKeys} onChange={setPropertyKeys} />

        <div className="mt-4 flex items-center gap-2">
          {COLLECTIONS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCollection(c.key)}
              aria-pressed={collection === c.key}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${collection === c.key ? 'bg-ink text-white' : 'text-ink-soft hover:bg-surface'}`}
            >
              {c.label}
            </button>
          ))}
          <label className="ml-auto flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-1.5">
            <Search size={14} className="text-ink-faint" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name"
              aria-label="Search documents"
              className="w-48 bg-transparent text-sm outline-none placeholder:text-ink-faint"
            />
          </label>
        </div>

        <div className="mt-4 overflow-visible rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-ink-faint">
                <th className="px-4 py-2 font-semibold">Name</th>
                <th className="w-28 px-2 py-2 font-semibold">Status</th>
                <th className="w-12 px-2 py-2 font-semibold">Rev</th>
                <th className="w-20 px-2 py-2 font-semibold">Size</th>
                <th className="w-24 px-2 py-2 font-semibold">Updated</th>
                <th className="w-36 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {docs.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-ink-faint">
                    Loading…
                  </td>
                </tr>
              )}
              {docs.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-ink-faint">
                    {q ? 'No documents match that name.' : 'No documents yet. Upload a lease to get started.'}
                  </td>
                </tr>
              )}
              {groups.map((p) => {
                const pOpen = !collapsed.has(p.key);
                return (
                  <Fragment key={p.key}>
                    <GroupBar label={p.label} count={p.count} open={pOpen} onToggle={() => toggle(p.key)} level={0} />
                    {pOpen &&
                      p.tenants.map((t) => {
                        const tKey = `${p.key}/${t.key}`;
                        const tOpen = !collapsed.has(tKey);
                        return (
                          <Fragment key={tKey}>
                            {t.label && (
                              <GroupBar
                                label={t.label}
                                count={t.docs.length}
                                open={tOpen}
                                onToggle={() => toggle(tKey)}
                                level={1}
                              />
                            )}
                            {(tOpen || !t.label) && t.docs.map((d) => <DocumentRow key={d.id} doc={d} />)}
                          </Fragment>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GroupBar(props: { label: string; count: number; open: boolean; onToggle: () => void; level: 0 | 1 }) {
  const Icon = props.open ? ChevronDown : ChevronRight;
  return (
    <tr
      className={
        props.level === 0 ? 'border-b border-line bg-surface-sunken' : 'border-b border-line-soft bg-surface-sunken/40'
      }
    >
      <td colSpan={6} className="p-0">
        <button
          type="button"
          onClick={props.onToggle}
          aria-expanded={props.open}
          className={`flex w-full items-center gap-2 py-2 text-left ${props.level === 0 ? 'px-4 font-semibold text-ink' : 'pl-8 pr-4 text-ink-soft'}`}
        >
          <Icon size={14} className="text-ink-faint" />
          <span>{props.label}</span>
          <span className="text-xs font-normal text-ink-faint">{props.count}</span>
        </button>
      </td>
    </tr>
  );
}

/** Multi-select: none ticked = every property. */
function PropertyFilter(props: { properties: Property[]; selected: string[]; onChange: (keys: string[]) => void }) {
  const { properties, selected, onChange } = props;
  const summary =
    selected.length === 0
      ? 'All properties'
      : selected.length === 1
        ? (properties.find((p) => p.key === selected[0])?.name ?? selected[0])
        : `${selected.length} properties`;
  return (
    <details className="relative mt-6 w-fit">
      <summary className="cursor-pointer list-none rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink">
        <span className="text-ink-faint">Property:</span> <span className="font-semibold">{summary}</span>
      </summary>
      <div className="absolute z-10 mt-1 min-w-56 rounded-lg border border-line bg-surface p-2 shadow-lg">
        {properties.map((p) => (
          <label
            key={p.key}
            className="flex items-center gap-2 rounded px-2 py-1 text-sm text-ink hover:bg-surface-sunken"
          >
            <input
              type="checkbox"
              checked={selected.includes(p.key)}
              onChange={(e) => onChange(e.target.checked ? [...selected, p.key] : selected.filter((k) => k !== p.key))}
            />
            {p.name}
          </label>
        ))}
        {selected.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="mt-1 px-2 text-xs text-ink-soft hover:text-ink">
            Show all
          </button>
        )}
      </div>
    </details>
  );
}

const STATUS: Record<LibraryDocument['status'], { label: string; tone: 'sage' | 'neutral' | 'faint' | 'bronze' }> = {
  indexed: { label: 'Searchable', tone: 'sage' },
  indexing: { label: 'Indexing', tone: 'neutral' },
  uploaded: { label: 'Uploaded', tone: 'neutral' },
  pending: { label: 'Uploading', tone: 'faint' },
  failed: { label: 'Index failed', tone: 'bronze' },
};

// A document stays `pending` from upload start until the file reaches storage.
// The upload URL is valid for 15 minutes (BFF SIGNED_URL_MINUTES); allow time
// for a large file still in flight, then treat it as a failed upload.
export const STUCK_UPLOAD_MS = 30 * 60 * 1000;

export function isStuckUpload(doc: Pick<LibraryDocument, 'status' | 'updatedAt'>, now = Date.now()): boolean {
  return doc.status === 'pending' && now - new Date(doc.updatedAt).getTime() > STUCK_UPLOAD_MS;
}

const UPLOAD_FAILED = { label: 'Upload failed', tone: 'bronze' as const };

function DocumentRow({ doc }: { doc: LibraryDocument }) {
  const { locked } = useSession();
  const writeLock = locked('documents.write');
  const replace = useReplaceDocument();
  const reindex = useReindexDocument();
  const remove = useDeleteDocument();
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // A replacement whose file name differs from the current one waits for a confirm.
  const [pendingReplace, setPendingReplace] = useState<File | null>(null);
  const [rejected, setRejected] = useState(false);
  const stuck = isStuckUpload(doc);
  const s = stuck ? UPLOAD_FAILED : STATUS[doc.status];
  const busy = replace.isPending || reindex.isPending || remove.isPending;

  const iconBtn =
    'rounded-md p-1.5 text-ink-faint hover:bg-surface-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <tr className="border-b border-line-soft last:border-0">
      <td className="px-4 py-2.5">
        <div className="font-semibold text-ink">{doc.name}</div>
        {doc.originalFileName !== doc.name && <div className="text-xs text-ink-faint">{doc.originalFileName}</div>}
        {stuck && (
          <div className="text-xs text-bronze">The file never arrived. Delete this entry and upload it again.</div>
        )}
        {rejected && <div className="text-xs text-bronze">{UNSUPPORTED_HINT}</div>}
      </td>
      <td className="px-2 py-2.5">
        <span title={doc.indexError}>
          <Pill tone={s.tone}>{replace.isPending ? 'Replacing…' : s.label}</Pill>
        </span>
      </td>
      <td className="px-2 py-2.5 text-ink-soft">{doc.revision}</td>
      <td className="px-2 py-2.5 text-ink-soft">{formatBytes(doc.sizeBytes)}</td>
      <td className="px-2 py-2.5 text-ink-soft">{formatDate(doc.updatedAt)}</td>
      <td className="relative px-2 py-2.5 text-right">
        <button
          type="button"
          className={iconBtn}
          aria-label={`Download ${doc.name}`}
          title="Download"
          disabled={doc.status === 'pending'}
          onClick={async () => {
            // Open the tab inside the click (popup blockers), then point it at the signed URL.
            const tab = window.open('', '_blank');
            try {
              const url = await downloadUrl(doc.id);
              if (tab) tab.location.href = url;
              else window.location.assign(url);
            } catch {
              tab?.close();
            }
          }}
        >
          <Download size={15} />
        </button>
        {doc.status === 'failed' && (
          <button
            type="button"
            className={iconBtn}
            aria-label="Retry indexing"
            title={writeLock ?? 'Retry indexing'}
            disabled={!!writeLock || busy}
            onClick={() => reindex.mutate(doc.id)}
          >
            <RefreshCw size={15} />
          </button>
        )}
        <button
          type="button"
          className={iconBtn}
          aria-label={`Replace ${doc.name}`}
          title={writeLock ?? 'Replace with a new revision'}
          disabled={!!writeLock || busy}
          onClick={() => fileRef.current?.click()}
        >
          <Replace size={15} />
        </button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept={SUPPORTED_EXTENSIONS.join(',')}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            setRejected(false);
            if (!f) return;
            if (!isSupportedFile(f.name)) {
              setRejected(true);
              return;
            }
            if (fileNameChanged(doc, f)) setPendingReplace(f);
            else replace.mutate({ id: doc.id, file: f });
          }}
        />
        <button
          type="button"
          className={iconBtn}
          aria-label={`Delete ${doc.name}`}
          title={writeLock ?? 'Delete'}
          disabled={!!writeLock || busy}
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 size={15} />
        </button>
        {pendingReplace && (
          <Confirm
            title="Replace with a differently named file?"
            body={`“${doc.originalFileName}” will be replaced by “${pendingReplace.name}” as revision ${doc.revision + 1}. The document keeps its name, “${doc.name}”.`}
            confirmLabel="Replace"
            busy={replace.isPending}
            onCancel={() => setPendingReplace(null)}
            onConfirm={() => {
              replace.mutate({ id: doc.id, file: pendingReplace });
              setPendingReplace(null);
            }}
          />
        )}
        {confirmDelete && (
          <Confirm
            title={`Delete “${doc.name}”?`}
            body="It is removed from Simon's search and storage, with every revision. This cannot be undone."
            confirmLabel="Delete"
            busy={remove.isPending}
            onCancel={() => setConfirmDelete(false)}
            onConfirm={() => remove.mutate(doc.id, { onSettled: () => setConfirmDelete(false) })}
          />
        )}
      </td>
    </tr>
  );
}
